import { useState } from 'react';

import { formatPriceWithCurrency } from '@vb-django/components/minskog/utils';

import { Button, Dialog, Icon, Text } from '@ui-component-library';

import { useCheckout } from '../context/CheckoutContext';

const getPaymentLabels = (paymentMethod?: string) => {
  if (paymentMethod === 'klarna') {
    return {
      subtitle: 'Vi skickar en bekräftelse till din e-post inom kort',
      email:
        'Vi skickar en kopia på din beställning till din mail. Kontrollera din skräpinkorg om du inte hittar den'
    };
  }
  return {
    subtitle: 'Vi kontaktar dig inom kort',
    contact:
      'Eftersom din fastighet är ovanligt stor vill vi ge dig en extra noggrann och personlig service'
  };
};

export const ThankYouStep = () => {
  const {
    selectedProducts,
    orderPrices,
    couponDiscount,
    paymentMethod,
    extraData,
    config: { type, summaryProductLabel, summaryAdditionalContent }
  } = useCheckout();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const totalArea = selectedProducts.reduce((acc, product) => acc + Number(product?.area ?? 0), 0);
  const paymentLabels = getPaymentLabels(paymentMethod);

  const selectedPackage = extraData.selectedPackage;
  const selectedForestManagementPlan = extraData.selectedForestManagementPlan;
  const isDrone = type === 'drone';

  return (
    <>
      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="flex flex-row items-center gap-2">
            <Icon name="CheckOutlined" size="xlarge" className="fill-success-700" />
            <Text variant="text-lg/leading-none/medium">Tack för din beställning!</Text>
          </div>
          <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
            {paymentLabels.subtitle}
          </Text>
        </div>

        <div className="h-[0.5px] w-full border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium">Sammanfattning</Text>

        <div className="bg-white-50 flex w-full flex-col gap-4 rounded-xl p-4">
          <div className="flex flex-row items-start justify-start gap-2">
            <Icon
              name="MapPinSimple"
              size="large"
              spacing="medium"
              className="fill-brown-700 bg-stone-200"
            />
            <div className="flex flex-1 flex-col justify-center gap-2">
              <Text variant="text-sm/leading-normal/medium">
                Fastighet ({selectedProducts.length})
              </Text>
              <div className="flex flex-col gap-2">
                {selectedProducts.map((p) => (
                  <Text
                    key={p.id}
                    variant="text-sm/leading-normal/normal"
                    className="text-pebble-500"
                  >
                    {p.name}
                  </Text>
                ))}
              </div>
            </div>
            {totalArea > 0 && (
              <Text variant="text-sm/leading-normal/normal" className="text-cloud-900 mb-3">
                {totalArea} ha
              </Text>
            )}
          </div>

          <Button variant="secondary" size="small" onClick={() => setIsDetailsOpen(true)}>
            Visa detaljer
          </Button>

          {selectedProducts.length > 0 && (
            <>
              <Text variant="text-sm/leading-normal/medium">Prisuppgifter</Text>
              {isDrone ? (
                <>
                  <div className="flex flex-row items-start justify-between gap-2">
                    <Text variant="text-sm/leading-normal/normal">Ortofoto</Text>
                    <Text variant="text-sm/leading-normal/medium">
                      {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                    </Text>
                  </div>
                  {selectedPackage && (
                    <div className="flex flex-row items-start justify-between gap-2">
                      <Text variant="text-sm/leading-normal/normal">
                        Skadeanalys {selectedPackage.title}
                      </Text>
                      <Text variant="text-sm/leading-normal/medium">
                        {formatPriceWithCurrency(orderPrices.packageTotalPrice)}
                      </Text>
                    </div>
                  )}
                  {selectedForestManagementPlan && (
                    <div className="flex flex-row items-start justify-between gap-2">
                      <Text variant="text-sm/leading-normal/normal">Skogsbruksplan</Text>
                      <Text variant="text-sm/leading-normal/medium">
                        {formatPriceWithCurrency(orderPrices.forestManagementPlanTotalPrice)}
                      </Text>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-row items-start justify-between gap-2">
                  <Text variant="text-sm/leading-normal/normal">{summaryProductLabel}</Text>
                  <Text variant="text-sm/leading-normal/medium">
                    {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                  </Text>
                </div>
              )}

              {summaryAdditionalContent}

              <div className="h-[0.5px] w-full border border-stone-500/17" />

              {isDrone && paymentMethod === 'invoice' ? (
                <div className="flex flex-row items-center justify-between gap-2">
                  <div className="flex flex-row gap-2">
                    <Text variant="text-sm/leading-normal/medium">Totalt</Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                      exkl. moms
                    </Text>
                  </div>
                  <div className="flex flex-row items-baseline gap-1">
                    {orderPrices.fullPrice !== null && orderPrices.fullPrice > 0 && (
                      <Text
                        variant="text-sm/leading-normal/medium"
                        className="text-pebble-500 line-through"
                      >
                        {formatPriceWithCurrency(orderPrices.fullPrice)}
                      </Text>
                    )}
                    <Text variant="text-sm/leading-normal/medium" className="text-pebble-500">
                      {formatPriceWithCurrency(orderPrices.discountPrice)}
                    </Text>
                  </div>
                </div>
              ) : (
                <div className="flex flex-row items-center justify-between gap-2">
                  <div className="flex flex-row gap-2">
                    <Text variant="text-sm/leading-normal/medium">Totalt</Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                      Inkl. moms
                    </Text>
                  </div>
                  <Text variant="text-sm/leading-normal/medium">
                    {formatPriceWithCurrency(
                      (orderPrices.discountPrice ?? 0) + (orderPrices.tax ?? 0)
                    )}
                  </Text>
                </div>
              )}
            </>
          )}
        </div>

        <div className="h-[0.5px] w-full border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium">Betalningsmetod</Text>
        {paymentMethod === 'klarna' ? (
          <img
            alt="Klarna logo"
            width="60px"
            height="24px"
            src={`${Window?.static_path ?? '/static/'}img/payment-provider/klarna.png`}
          />
        ) : (
          <div className="bg-charcoal-500 text-white-50 flex w-fit flex-row rounded-md px-2 py-1">
            <Text variant="text-xs/leading-normal/normal">Faktura</Text>
          </div>
        )}

        <div className="h-[0.5px] w-full border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium">Vad händer nu?</Text>

        <div className="bg-white-50 flex w-full flex-col gap-4 rounded-xl p-4">
          {paymentLabels.contact && (
            <div className="flex flex-row items-start gap-4">
              <Icon name="PhoneCallSimple" size="large" />
              <div className="flex flex-col gap-1">
                <Text variant="text-base/leading-normal/medium">Vi kontaktar dig</Text>
                <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                  {paymentLabels.contact}
                </Text>
              </div>
            </div>
          )}
          {paymentLabels.email && (
            <div className="flex flex-row items-start gap-4">
              <Icon name="MailSimple" size="large" />
              <div className="flex flex-col gap-1">
                <Text variant="text-base/leading-normal/medium">Bekräftelsemail</Text>
                <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                  {paymentLabels.email}
                </Text>
              </div>
            </div>
          )}
        </div>

        <div className="h-[0.5px] w-full border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium">Behöver du hjälp?</Text>

        <div className="bg-white-50 flex w-full flex-col overflow-hidden rounded-xl">
          <a
            href="mailto:information@treebula.se"
            className="hover:bg-white-500 flex flex-row items-center justify-start gap-4 p-4"
          >
            <Icon name="MailSimple" size="large" />
            <div className="flex flex-1 flex-col gap-1">
              <Text variant="text-base/leading-normal/medium">E-post</Text>
              <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                information@treebula.se
              </Text>
            </div>
            <Icon name="ChevronDownSimple" size="large" className="-rotate-90" />
          </a>
          <div className="h-[0.5px] w-full border border-stone-500/17" />
          <a
            className="hover:bg-white-500 flex flex-row items-center justify-start gap-4 p-4"
            href="tel:08339944"
          >
            <Icon name="CustomerSupportSimple" size="large" />
            <div className="flex flex-1 flex-col gap-1">
              <Text variant="text-base/leading-normal/medium">Ring</Text>
              <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                08 - 33 99 44
              </Text>
            </div>
            <Icon name="ChevronDownSimple" size="large" className="-rotate-90" />
          </a>
        </div>
      </div>

      <Dialog isOpen={isDetailsOpen} size="small">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="text-base/leading-normal/medium">Prisdetaljer</Text>
            <Button variant="secondary" size="icon" onClick={() => setIsDetailsOpen(false)}>
              <Icon name="CrossSimple" className="color-charcoal-500" size="medium" />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className="flex h-full flex-col gap-4">
            {isDrone && totalArea <= 30 && (
              <div className="flex flex-row gap-2 rounded-md bg-stone-100 p-3">
                <Icon name="InfoFilled" size="medium" />
                <div className="flex flex-col gap-2">
                  <Text variant="text-xs/leading-normal/normal">
                    För att kunna genomföra en kostnadseffektiv pilotutkörning och täcka
                    resekostnader samt övriga fasta moment i uppstarten, kan vi tyvärr aldrig ta
                    betalt för mindre än 30 hektar
                  </Text>
                </div>
              </div>
            )}
            {isDrone && (
              <>
                <div className="flex flex-row items-center justify-between gap-2">
                  <Text variant="text-sm/leading-normal/normal">
                    {`Ortofoto (${String(orderPrices.propertiesHectarePrice ?? '')}/ha)`}
                  </Text>
                  <Text variant="text-sm/leading-normal/medium">
                    {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                  </Text>
                </div>
                {(selectedPackage || selectedForestManagementPlan) && (
                  <div className="h-[0.5px] w-full border border-stone-500/17" />
                )}
                {selectedPackage && (
                  <div className="flex flex-row items-center justify-between gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      {`Skadeanalys ${selectedPackage.title ?? ''} (${String(orderPrices.packageHectarePrice ?? '')}/ha)`}
                    </Text>
                    <Text variant="text-sm/leading-normal/medium">
                      {formatPriceWithCurrency(orderPrices.packageTotalPrice)}
                    </Text>
                  </div>
                )}
                {selectedForestManagementPlan && (
                  <div className="flex flex-row items-center justify-between gap-2">
                    <Text variant="text-sm/leading-normal/normal">Skogsbruksplan (99 kr/ha)</Text>
                    <Text variant="text-sm/leading-normal/medium">
                      {formatPriceWithCurrency(orderPrices.forestManagementPlanTotalPrice)}
                    </Text>
                  </div>
                )}
                <div className="h-[0.5px] w-full border border-stone-500/17" />
              </>
            )}
            {couponDiscount && (
              <div className="flex flex-row items-center justify-between gap-2">
                <Text variant="text-sm/leading-normal/normal">
                  Rabattkod {couponDiscount.coupon_code}
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  -{formatPriceWithCurrency(couponDiscount.coupon_discount_price)}
                </Text>
              </div>
            )}
            <div className="flex flex-row items-center justify-between gap-2">
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                Moms
              </Text>
              <Text variant="text-sm/leading-normal/medium">
                {formatPriceWithCurrency(orderPrices.tax)}
              </Text>
            </div>
            <div className="flex flex-row items-center justify-between gap-2">
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                Inkl. moms
              </Text>
              <Text variant="text-sm/leading-normal/medium">
                {formatPriceWithCurrency((orderPrices.discountPrice ?? 0) + (orderPrices.tax ?? 0))}
              </Text>
            </div>
            <div className="mt-auto h-[0.5px] w-full border border-stone-500/17" />
            <div className="flex flex-row items-center justify-between gap-2">
              <Text variant="text-sm/leading-normal/medium">Totalt</Text>
              <div className="flex flex-col items-end gap-1">
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.discountPrice)}
                </Text>
                <Text variant="text-xs/leading-normal/normal" className="text-charcoal-300">
                  exkl. moms
                </Text>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
